import { Text, useBreakpointValue, useDisclosure } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import * as React from 'react';
import Button from '~/components/Button';

import { CalendarIcon } from '../icons';
import DesktopDatePicker from './DesktopDatePicker';
import MobileDatePicker from './MobileDatePicker';
import { DATE_FORMAT, START_DATE } from './utils';

const propTypes = {
  startDate: PropTypes.any,
  endDate: PropTypes.any,
  onDatesChange: PropTypes.func.isRequired,
  fromPlaceholder: PropTypes.string.isRequired,
  toPlaceholder: PropTypes.string.isRequired,
  controllerProps: PropTypes.object,
  calendarIconProps: PropTypes.object,
};

const MainButton = React.forwardRef((props, ref) => (
  <Button
    border="1px solid black"
    borderRadius="20px"
    backgroundColor="white"
    fontSize="md"
    textTransform="none"
    fontWeight="normal"
    letterSpacing="0"
    _hover={{ backgroundColor: 'white' }}
    _active={{ backgroundColor: 'white' }}
    ref={ref}
    justifyContent="space-between"
    {...props}
  />
));

const DateRangePicker = ({
  startDate: initialStartDate,
  endDate: initialEndDate,
  onDatesChange,
  fromPlaceholder,
  toPlaceholder,
  controllerProps = {},
  calendarIconProps = {},
  ...restOfProps
}) => {
  const isMobileView = useBreakpointValue({ base: true, md: false });
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [startDate, setStartDate] = React.useState(initialStartDate);
  const [endDate, setEndDate] = React.useState(initialEndDate);

  const [focusedInput, setFocusedInput] = React.useState(START_DATE);

  const handleInitialize = () => {
    onOpen();
    setFocusedInput(START_DATE);
    setStartDate(initialStartDate);
    setEndDate(initialEndDate);
  };
  const handleDateChange = (newValues) => {
    setStartDate(newValues.startDate);
    setEndDate(newValues.endDate);
  };

  const handleApply = () => {
    onDatesChange({ startDate, endDate });
  };
  const handleClear = () => {
    onDatesChange({ startDate: null, endDate: null });
  };

  const getButtonText = () => {
    if (!initialStartDate && !initialEndDate) {
      return 'Any date';
    }
    if (initialStartDate && !initialEndDate) {
      return `${initialStartDate?.format(DATE_FORMAT)} - ${toPlaceholder}`;
    }
    if (!initialStartDate && initialEndDate) {
      return `${fromPlaceholder} - ${initialEndDate?.format(DATE_FORMAT)}`;
    }
    if (initialStartDate?.year() === initialEndDate?.year()) {
      return `${initialStartDate?.format('D MMM')} - ${initialEndDate?.format(
        DATE_FORMAT,
      )}`;
    }
    return `${initialStartDate?.format(DATE_FORMAT)} - ${initialEndDate?.format(
      DATE_FORMAT,
    )}`;
  };

  const hasDateSet = !!(initialStartDate || initialEndDate);
  const buttonText = getButtonText();
  const calendarIcon = (
    <CalendarIcon
      color={hasDateSet ? 'primary.500' : 'gray.400'}
      marginLeft={4}
      boxSize={5}
      {...calendarIconProps}
    />
  );

  const mainButton = (
    <MainButton
      onClick={handleInitialize}
      color={hasDateSet ? 'secondary.500' : 'gray.500'}
      borderColor={isOpen ? 'primary.500' : 'gray.400'}
      fontSize={{ base: 'lg', sm: 'md' }}
      // Note how rest of props are spread into this button
      {...restOfProps}
    >
      <Text>{buttonText}</Text>
      {calendarIcon}
    </MainButton>
  );

  const props = {
    startDate,
    endDate,
    onDatesChange,
    fromPlaceholder,
    toPlaceholder,
    controllerProps,
    onClear: handleClear,
    onApply: handleApply,
    onDateChange: handleDateChange,
    focusedInput,
    onFocusedInputChange: setFocusedInput,
    hasDateSet,
    isOpen,
    onOpen,
    onClose,
    mainButton,
  };

  return (
    <>
      {!isMobileView ? (
        <DesktopDatePicker {...props} />
      ) : (
        <MobileDatePicker {...props} />
      )}
    </>
  );
};

DateRangePicker.propTypes = propTypes;

export default DateRangePicker;
